import { Link } from "@/components/LangLink";
import { ArrowRight, ShieldCheck, ClipboardCheck, HeartHandshake, TrendingUp } from "lucide-react";
import { Reveal } from "@/components/primitives";
import {
  CERT_PUBLISHED, CERT_EYEBROW, CERT_STANDARD, CERT_BOX_TITLE,
  CERT_SECTION, CERT_HOME, CERT_LINK,
} from "@/lib/certifications";

const POINT_ICONS = [ClipboardCheck, HeartHandshake, TrendingUp];

// Riquadro testuale istituzionale (provvisorio) — NON un logo ufficiale.
// Rettangolare, bordo sottile, testo su due righe.
const IsoTextMark = ({ className = "" }) => (
  <div
    className={`inline-flex flex-col items-center justify-center rounded-lg border border-navy/20 bg-white px-8 py-6 text-center ${className}`}
    data-testid="cert-iso-mark"
    aria-label={`${CERT_BOX_TITLE} — ${CERT_STANDARD}`}
  >
    <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-navy/55 leading-relaxed">
      {CERT_BOX_TITLE}
    </span>
    <span className="mt-2 h-px w-10 bg-green/50" />
    <span className="mt-2 font-serif text-lg font-bold tracking-tight text-navy">
      {CERT_STANDARD}
    </span>
  </div>
);

// --- A) Sezione completa (pagina Chi Siamo) --------------------------------
export const CertificationsSection = () => {
  if (!CERT_PUBLISHED) return null;
  return (
    <section id="certificazioni" className="scroll-mt-28 py-20 lg:py-28 bg-cloud" data-testid="certificazioni-section">
      <div className="container-x">
        <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_320px] lg:items-start">
          {/* Testo */}
          <Reveal>
            <span className="eyebrow">{CERT_EYEBROW}</span>
            <h2 className="mt-4 font-serif text-3xl lg:text-4xl font-bold text-navy leading-tight">
              {CERT_SECTION.title}
            </h2>
            <div className="mt-6 space-y-4 text-muted-foreground leading-relaxed">
              {CERT_SECTION.paragraphs.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </Reveal>

          {/* Riquadro ISO provvisorio */}
          <Reveal delay={0.1} className="lg:pt-2">
            <div className="flex justify-center rounded-3xl border border-[#E7EDF2] bg-white p-8 lg:justify-start">
              <IsoTextMark />
            </div>
          </Reveal>
        </div>

        {/* Tre punti */}
        <div className="mt-12 grid gap-6 sm:grid-cols-3" data-testid="certificazioni-punti">
          {CERT_SECTION.points.map((pt, i) => {
            const Icon = POINT_ICONS[i];
            return (
              <div key={pt.title} className="rounded-2xl border border-[#E7EDF2] bg-white p-7 h-full" data-testid={`cert-punto-${i}`}>
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-green/12 text-green"><Icon size={22} /></span>
                <h3 className="mt-5 font-serif text-lg font-bold text-navy">{pt.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{pt.desc}</p>
              </div>
            );
          })}
        </div>

        {/* Nota discreta */}
        <p className="mt-8 flex items-start gap-2 text-xs text-muted-foreground leading-relaxed max-w-3xl">
          <ShieldCheck size={15} className="mt-0.5 shrink-0 text-green" />
          {CERT_SECTION.note}
        </p>
      </div>
    </section>
  );
};

// --- B) Richiamo sintetico (Home) ------------------------------------------
export const CertificationsHomeCallout = () => {
  if (!CERT_PUBLISHED) return null;
  return (
    <section className="py-16 lg:py-20 bg-white" data-testid="certificazioni-home">
      <div className="container-x">
        <div className="flex flex-col items-start gap-8 rounded-3xl border border-[#E7EDF2] bg-cloud p-8 lg:flex-row lg:items-center lg:justify-between lg:p-10">
          <div className="max-w-2xl">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-green-deep">{CERT_EYEBROW}</div>
            <h3 className="mt-2 font-serif text-2xl lg:text-3xl font-bold text-navy leading-tight">{CERT_HOME.title}</h3>
            <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{CERT_HOME.text}</p>
            <Link to={CERT_LINK} data-testid="certificazioni-home-link" className="btn-outline mt-6 inline-flex">
              {CERT_HOME.cta} <ArrowRight size={16} />
            </Link>
          </div>
          <div className="shrink-0">
            <IsoTextMark />
          </div>
        </div>
      </div>
    </section>
  );
};

// --- C) Link nel Footer ----------------------------------------------------
export const CertificationsFooterLink = ({ className = "" }) => {
  if (!CERT_PUBLISHED) return null;
  return (
    <Link to={CERT_LINK} data-testid="footer-certificazioni" className={className}>
      Certificazioni
    </Link>
  );
};
